import { DOMOutputSpec, DOMSerializer, Node as ProseMirrorNode } from 'prosemirror-model';

/**
 * Configuration of the token HTML and TipTap AST structure.
 * This is the single isolated place that needs to be updated if the Pixl-Editor's
 * token HTML or AST representation changes in future releases.
 */
export interface TokenHtmlDefinition {
  /** TipTap ProseMirror node name */
  nodeName: string
  /** Primary HTML tag name for generation and matching */
  tagName: string
  /** Attribute name for the token ID */
  idAttribute: string
  /** Attribute name for the token solution */
  solutionAttribute: string
  /** Optional fallback attribute names for backward compatibility */
  fallbackIdAttributes?: string[]
  fallbackSolutionAttributes?: string[]
  /** Extra attributes to include in generated HTML (e.g. data-type="calculator-solution") */
  extraAttributes?: Record<string, string>
  /** CSS selector for querying DOM elements */
  domSelector: string
}

export const DEFAULT_TOKEN_HTML_CONFIG: TokenHtmlDefinition = {
  nodeName: 'calculatorSolution',
  tagName: 'calculator-solution',
  idAttribute: 'data-id',
  solutionAttribute: 'data-w',
  fallbackIdAttributes: ['id'],
  fallbackSolutionAttributes: ['w'],
  extraAttributes: { 'data-type': 'calculator-solution' },
  domSelector: 'calculator-solution, span[data-type="calculator-solution"], span[data-calculator-solution]',
};

export interface ExtractedToken {
  id: string
  solution: string
  pos?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node?: any
}

/**
 * Adapter encapsulating all HTML, DOM and TipTap AST interactions for Taschenrechner tokens.
 */
export class TaschenrechnerTokenHtmlAdapter {
  constructor(public readonly config: TokenHtmlDefinition = DEFAULT_TOKEN_HTML_CONFIG) {}

  /**
   * Generates the configured HTML tag for a token.
   */
  public toHtml(token: { id: string, solution: string }): string {
    const escapedId = this.escapeAttr(token.id);
    const escapedSolution = this.escapeAttr(token.solution);
    const attrs: string[] = [];

    if (this.config.extraAttributes) {
      for (const [key, val] of Object.entries(this.config.extraAttributes)) {
        attrs.push(`${key}="${this.escapeAttr(val)}"`);
      }
    }

    attrs.push(`${this.config.idAttribute}="${escapedId}"`);
    attrs.push(`${this.config.solutionAttribute}="${escapedSolution}"`);

    return `<${this.config.tagName} ${attrs.join(' ')}></${this.config.tagName}>`;
  }

  /**
   * Converts ZAV token format {id:"..." w:"..."} in text to the configured TipTap HTML format.
   */
  public fromZavToHtml(text: string): string {
    if (!text) return '';
    const regex = /\{\s*id\s*:\s*["']([^"']+)["']\s*,?\s*w\s*:\s*["']([^"']*)["']\s*\}/gi;
    return text.replace(regex, (_match, id, w) => {
      return this.toHtml({ id, solution: w });
    });
  }

  /**
   * Robust parser that converts any token HTML markup emitted by the editor into the standard
   * ZAV format {id:"..." w:"..."} for persistence and player preview.
   * Handles arbitrary attribute order, whitespace, and fallback selectors.
   */
  public fromHtmlToZav(html: string): string {
    if (!html) return '';

    if (typeof DOMParser !== 'undefined') {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const elements = doc.body.querySelectorAll(this.config.domSelector);
        if (elements.length > 0) {
          elements.forEach(el => {
            const id = this.extractIdFromElement(el);
            const solution = this.extractSolutionFromElement(el);
            if (id) {
              const tokenText = `{id:"${id}" w:"${solution}"}`;
              const textNode = doc.createTextNode(tokenText);
              el.parentNode?.replaceChild(textNode, el);
            }
          });
          return doc.body.innerHTML;
        }
      }
      catch {
        // Fall back to regex parsing
      }
    }

    return this.regexFromHtmlToZav(html);
  }

  /**
   * Extracts all tokens from an HTML string in order of appearance.
   */
  public extractTokensFromHtml(html: string): ExtractedToken[] {
    if (!html) return [];

    const tokens: ExtractedToken[] = [];
    const seenIds = new Set<string>();

    if (typeof DOMParser !== 'undefined') {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const elements = doc.body.querySelectorAll(this.config.domSelector);
        elements.forEach(el => {
          const id = this.extractIdFromElement(el);
          const solution = this.extractSolutionFromElement(el);
          if (id && !seenIds.has(id)) {
            tokens.push({ id, solution });
            seenIds.add(id);
          }
        });
      }
      catch {
        // Fall back to regex
      }
    }

    if (tokens.length === 0) {
      const regexTokens = this.regexExtractTokens(html);
      for (const t of regexTokens) {
        if (!seenIds.has(t.id)) {
          tokens.push(t);
          seenIds.add(t.id);
        }
      }
    }

    // Also extract any raw ZAV tokens {id:"..." w:"..."} that might be in the string
    const zavRegex = /\{\s*id\s*:\s*["']([^"']+)["']\s*,?\s*w\s*:\s*["']([^"']*)["']\s*}/gi;
    let match: RegExpExecArray | null;
    while ((match = zavRegex.exec(html)) !== null) {
      const id = match[1];
      const solution = match[2];
      if (id && !seenIds.has(id)) {
        tokens.push({ id, solution });
        seenIds.add(id);
      }
    }

    return tokens;
  }

  /**
   * Checks whether a ProseMirror / TipTap node represents a token.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public isTokenNode(node: any): boolean {
    if (!node) return false;
    if (node.type?.name === this.config.nodeName) {
      return true;
    }
    // Check attrs if node name was customized or differs
    const attrs = node.attrs;
    if (attrs && typeof attrs === 'object') {
      const hasId = attrs[this.config.idAttribute] !== undefined ||
        attrs.id !== undefined ||
        (this.config.fallbackIdAttributes?.some(attr => attrs[attr] !== undefined) ?? false);
      const hasSolution = attrs[this.config.solutionAttribute] !== undefined ||
        attrs.w !== undefined ||
        (this.config.fallbackSolutionAttributes?.some(attr => attrs[attr] !== undefined) ?? false);
      if (hasId && hasSolution) {
        return true;
      }
    }
    return false;
  }

  /**
   * Extracts token ID and solution from a TipTap / ProseMirror node.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public extractTokenFromNode(node: any, pos?: number): ExtractedToken | null {
    if (!this.isTokenNode(node)) return null;

    const attrs = node.attrs ?? {};
    let id: string | undefined = attrs[this.config.idAttribute];
    if (id === undefined && this.config.fallbackIdAttributes) {
      for (const fallback of this.config.fallbackIdAttributes) {
        if (attrs[fallback] !== undefined) {
          id = attrs[fallback];
          break;
        }
      }
    }
    if (id === undefined && attrs.id !== undefined) {
      id = attrs.id;
    }

    let solution: string | undefined = attrs[this.config.solutionAttribute];
    if (solution === undefined && this.config.fallbackSolutionAttributes) {
      for (const fallback of this.config.fallbackSolutionAttributes) {
        if (attrs[fallback] !== undefined) {
          solution = attrs[fallback];
          break;
        }
      }
    }
    if (solution === undefined && attrs.w !== undefined) {
      solution = attrs.w;
    }

    if (!id) return null;

    return {
      id: String(id),
      solution: String(solution ?? ''),
      pos,
      node,
    };
  }

  /**
   * Traverses a ProseMirror / TipTap document and extracts all active token nodes.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public extractAllTokensFromDoc(doc: any): ExtractedToken[] {
    const tokens: ExtractedToken[] = [];
    if (!doc || typeof doc.descendants !== 'function') return tokens;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    doc.descendants((node: any, pos: number) => {
      if (this.isTokenNode(node)) {
        const token = this.extractTokenFromNode(node, pos);
        if (token) {
          tokens.push(token);
        }
        return false; // Atom node, no child recursion
      }
      return true;
    });

    return tokens;
  }

  /**
   * Returns updated attributes for a ProseMirror node transaction (setNodeMarkup).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateNodeAttrs(attrs: Record<string, any>, newSolution: string): Record<string, any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updated: Record<string, any> = { ...attrs };
    updated[this.config.solutionAttribute] = newSolution;
    updated.w = newSolution;
    if (this.config.fallbackSolutionAttributes) {
      for (const fallback of this.config.fallbackSolutionAttributes) {
        updated[fallback] = newSolution;
      }
    }
    return updated;
  }

  /**
   * Serializes the document of the given TipTap editor to HTML.
   * The token node is rendered by this adapter (guaranteeing non-empty id and solution attributes),
   * all other nodes and marks use the editor's own schema serializer. The serializer is created per call
   * and scoped to the given editor, so no global or schema-internal state is modified.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public serializeEditorHtml(editor: any): string {
    const schema = editor?.schema;
    const content = editor?.state?.doc?.content;
    if (!schema?.nodes || !content || typeof document === 'undefined') {
      return typeof editor?.getHTML === 'function' ? editor.getHTML() : '';
    }

    const baseSerializer = DOMSerializer.fromSchema(schema);
    const nodes = schema.nodes[this.config.nodeName] ?
      { ...baseSerializer.nodes, [this.config.nodeName]: (node: ProseMirrorNode) => this.tokenNodeToDom(node) } :
      baseSerializer.nodes;
    const serializer = new DOMSerializer(nodes, baseSerializer.marks);

    const container = document.createElement('div');
    container.appendChild(serializer.serializeFragment(content));
    return container.innerHTML;
  }

  /**
   * Returns attribute map for creating a new TipTap node.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createNodeAttrs(id: string, solution: string): Record<string, any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const attrs: Record<string, any> = {
      ...(this.config.extraAttributes ?? {}),
      [this.config.idAttribute]: id,
      [this.config.solutionAttribute]: solution,
    };
    if (this.config.idAttribute !== 'id') {
      attrs.id = id;
    }
    if (this.config.solutionAttribute !== 'w') {
      attrs.w = solution;
    }
    return attrs;
  }

  private tokenNodeToDom(node: ProseMirrorNode): DOMOutputSpec {
    const extracted = this.extractTokenFromNode(node);
    const id = extracted?.id ?? node.attrs?.id ?? '';
    const solution = extracted?.solution ?? node.attrs?.w ?? '';
    return [
      this.config.tagName,
      {
        ...(this.config.extraAttributes ?? {}),
        [this.config.idAttribute]: id,
        [this.config.solutionAttribute]: solution,
      },
    ];
  }

  private extractIdFromElement(el: Element): string | null {
    let id = el.getAttribute(this.config.idAttribute);
    if (!id && this.config.fallbackIdAttributes) {
      for (const fallback of this.config.fallbackIdAttributes) {
        id = el.getAttribute(fallback);
        if (id) break;
      }
    }
    if (!id) {
      id = el.getAttribute('id');
    }
    return id;
  }

  private extractSolutionFromElement(el: Element): string {
    let w = el.getAttribute(this.config.solutionAttribute);
    if (w === null && this.config.fallbackSolutionAttributes) {
      for (const fallback of this.config.fallbackSolutionAttributes) {
        w = el.getAttribute(fallback);
        if (w !== null) break;
      }
    }
    if (w === null) {
      w = el.getAttribute('w');
    }
    return w ?? '';
  }

  private escapeAttr(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private unescapeAttr(value: string): string {
    return value
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  private regexFromHtmlToZav(html: string): string {
    // Regex matching any token element tag with opening and optional closing tag
    const tagPattern = new RegExp(`<(${this.config.tagName}|span)([^>]*)>(?:<\\/\\1>)?`, 'gi');
    return html.replace(tagPattern, (_match, _tagName, attrStr) => {
      const idMatch = new RegExp(`(?:${this.config.idAttribute}|id)=["']([^"']+)["']`, 'i').exec(attrStr);
      const solMatch = new RegExp(`(?:${this.config.solutionAttribute}|w)=["']([^"']*)["']`, 'i').exec(attrStr);
      if (idMatch) {
        const id = this.unescapeAttr(idMatch[1]);
        const sol = solMatch ? this.unescapeAttr(solMatch[1]) : '';
        return `{id:"${id}" w:"${sol}"}`;
      }
      return _match;
    });
  }

  private regexExtractTokens(html: string): ExtractedToken[] {
    const tokens: ExtractedToken[] = [];
    const tagPattern = new RegExp(`<(${this.config.tagName}|span)([^>]*)>(?:<\\/\\1>)?`, 'gi');
    let match: RegExpExecArray | null;
    while ((match = tagPattern.exec(html)) !== null) {
      const attrStr = match[2];
      const idMatch = new RegExp(`(?:${this.config.idAttribute}|id)=["']([^"']+)["']`, 'i').exec(attrStr);
      const solMatch = new RegExp(`(?:${this.config.solutionAttribute}|w)=["']([^"']*)["']`, 'i').exec(attrStr);
      if (idMatch) {
        tokens.push({
          id: this.unescapeAttr(idMatch[1]),
          solution: solMatch ? this.unescapeAttr(solMatch[1]) : '',
        });
      }
    }
    return tokens;
  }
}

export const defaultTokenHtmlAdapter = new TaschenrechnerTokenHtmlAdapter();
